import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { facebookCampaignsService, CreateFbCampaignPayload } from '@/services/facebook-campaigns.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { CampaignStatusPill } from '@/components/facebook/CampaignStatusPill';
import toast from 'react-hot-toast';
import { Check, Circle, ChevronRight, Megaphone, DollarSign, CalendarClock, ClipboardList, Info, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { optGoalsForObjective, goalLabel, pixelEventsForObjective, type OdaxObjective } from '@/lib/meta-matrix';

const OBJECTIVES: Array<{ value: CreateFbCampaignPayload['objective']; label: string; description: string }> = [
  { value: 'OUTCOME_AWARENESS', label: 'Awareness', description: 'Reach + brand awareness' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic', description: 'Drive clicks to your website or app' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement', description: 'Likes, comments, shares, video views, messages' },
  { value: 'OUTCOME_LEADS', label: 'Leads', description: 'Capture lead form submissions' },
  { value: 'OUTCOME_APP_PROMOTION', label: 'App Promotion', description: 'Installs + in-app events' },
  { value: 'OUTCOME_SALES', label: 'Sales', description: 'Conversions, catalog sales' },
];

const SPECIAL_CATEGORIES = [
  { value: 'NONE', label: 'None (default)', hint: '' },
  { value: 'EMPLOYMENT', label: 'Employment',
    hint: 'Restricts targeting: cannot set age, gender, or ZIP code. Required by Meta for job ads under US ADP rules.' },
  { value: 'HOUSING', label: 'Housing',
    hint: 'Restricts targeting: cannot set age, gender, or ZIP code. Required by Meta for housing ads.' },
  // Meta retired CREDIT (live 2026-09-30) — credit, loans and insurance now go under Financial products & services.
  { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Financial products & services',
    hint: 'Credit cards, loans, insurance, investments. Restricts targeting: cannot set age, gender, or ZIP.' },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Politics / social issues',
    hint: 'Requires advertiser identity verification + country-of-issue authorization (set up separately in Meta Business Settings).' },
];

const STEPS = [
  { label: 'Basics', icon: <Megaphone size={15} /> },
  { label: 'Budget', icon: <DollarSign size={15} /> },
  { label: 'Schedule', icon: <CalendarClock size={15} /> },
  { label: 'Review', icon: <ClipboardList size={15} /> },
];

export default function FacebookCampaignBuilder() {
  const nav = useNavigate();
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  const [step, setStep] = useState(0);

  // Step 1 — Basics
  const [name, setName] = useState('');
  const [objective, setObjective] = useState<CreateFbCampaignPayload['objective']>('OUTCOME_TRAFFIC');
  const [specialCategory, setSpecialCategory] = useState('NONE');

  // Step 2 — Budget
  const [budgetMode, setBudgetMode] = useState<'daily' | 'lifetime'>('daily');
  const [budgetMajor, setBudgetMajor] = useState<string>('50');
  const [status, setStatus] = useState<'ACTIVE' | 'PAUSED'>('PAUSED');

  // Step 3 — Schedule
  const [startTime, setStartTime] = useState('');
  const [stopTime, setStopTime] = useState('');

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
  }, []);

  // Meta reports the account currency; until it does, show '—' rather than guess.
  const currency = account?.currency ?? '';

  const stepValid = (s: number) => {
    if (s === 0) return !!name && !!objective;
    if (s === 1) return !!budgetMajor && parseFloat(budgetMajor) > 0;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const major = parseFloat(budgetMajor || '0');
      if (isNaN(major) || major <= 0) {
        toast.error('Enter a valid budget');
        setBusy(false);
        return;
      }
      const minor = String(Math.round(major * 100));

      const payload: CreateFbCampaignPayload = {
        name,
        objective,
        status,
        special_ad_categories: [specialCategory],
        buying_type: 'AUCTION',
      };
      if (budgetMode === 'daily') payload.daily_budget = minor;
      else payload.lifetime_budget = minor;
      if (startTime) payload.start_time = new Date(startTime).toISOString();
      if (stopTime) payload.stop_time = new Date(stopTime).toISOString();

      const r = await facebookCampaignsService.create(payload);
      toast.success(`Campaign created · status ${status}`);
      nav(`/facebook/campaigns/${r.id}`);
    } catch {
      /* toast shown by axios interceptor */
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Topbar
        title="New Facebook Campaign"
        breadcrumb="Facebook Ads / Campaigns / New"
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav('/facebook/campaigns')}>
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                disabled={!stepValid(step)}
                onClick={() => setStep((s) => s + 1)}
                iconRight={<ChevronRight size={14} />}
              >
                Next
              </Button>
            ) : (
              <Button loading={busy} onClick={submit}>
                Create campaign
              </Button>
            )}
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[800px] px-7 py-7 space-y-5">
        {/* Stepper */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s.label} className="flex items-center gap-2">
              <button
                onClick={() => { if (i < step || stepValid(i)) setStep(i); }}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors',
                  i < step
                    ? 'bg-gold-gradient text-on-primary shadow-soft'
                    : i === step
                    ? 'border border-primary bg-primary-soft text-primary'
                    : 'border border-border text-text-muted',
                )}
              >
                {i < step ? <Check size={13} /> : i === step ? s.icon : <Circle size={13} />}
                {s.label}
              </button>
              {i < STEPS.length - 1 && (
                <div className={clsx('h-px w-6 transition-colors', i < step ? 'bg-primary' : 'bg-border')} />
              )}
            </div>
          ))}
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-3 rounded-xl border border-info/30 bg-info-soft px-4 py-3">
          <Info size={15} className="mt-0.5 shrink-0 text-info" />
          <p className="text-xs text-info">
            <strong>Heads up:</strong> Meta requires at least one <em>ad set</em> + <em>ad</em> for your campaign to deliver.
            After creating the campaign you can add those here or in{' '}
            <a
              href="https://business.facebook.com/adsmanager"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Meta Ads Manager
            </a>
            .
          </p>
        </div>

        {/* Step 1 — Basics */}
        {step === 0 && (
          <Card>
            <SectionHeader title="Campaign basics" icon={<Megaphone size={16} />} />
            <FormField label="Campaign name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mi Coders — Black Friday Promo"
                maxLength={255}
              />
            </FormField>
            <FormField label="Objective" required>
              <Select
                value={objective}
                onChange={(e) => setObjective(e.target.value as CreateFbCampaignPayload['objective'])}
              >
                {OBJECTIVES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label} — {o.description}
                  </option>
                ))}
              </Select>
              <p className="mt-1.5 text-2xs text-text-muted">
                Ad sets under this campaign will be limited to these optimization goals:{' '}
                <span className="font-mono text-text-secondary">
                  {optGoalsForObjective(objective as OdaxObjective).slice(0, 4).map(goalLabel).join(', ')}
                  {optGoalsForObjective(objective as OdaxObjective).length > 4 && ` +${optGoalsForObjective(objective as OdaxObjective).length - 4} more`}
                </span>
              </p>
            </FormField>
            <FormField label="Special ad category" hint="Required by Meta for regulated industries">
              <Select value={specialCategory} onChange={(e) => setSpecialCategory(e.target.value)}>
                {SPECIAL_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              {specialCategory !== 'NONE' && (
                <div className="mt-1.5 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>{SPECIAL_CATEGORIES.find(c => c.value === specialCategory)?.hint}</span>
                </div>
              )}
            </FormField>

            {/* What this objective enables — full matrix preview */}
            <CascadePreview objective={objective as OdaxObjective} />
          </Card>
        )}

        {/* Step 2 — Budget */}
        {step === 1 && (
          <Card>
            <SectionHeader title="Budget & status" icon={<DollarSign size={16} />} />
            <div className="mb-4 flex gap-3">
              {(['daily', 'lifetime'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setBudgetMode(mode)}
                  className={clsx(
                    'flex-1 rounded-lg border py-3 text-sm font-semibold transition-all',
                    budgetMode === mode
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-muted hover:bg-surface-alt',
                  )}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)} budget
                </button>
              ))}
            </div>
            <FormField label={`Budget amount (${currency || '—'})`} required hint={`Min ~${currency === 'USD' ? '$1' : '5 ' + currency} for most countries`}>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={budgetMajor}
                onChange={(e) => setBudgetMajor(e.target.value)}
                placeholder="50.00"
              />
            </FormField>
            <FormField label="Initial status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'PAUSED')}>
                <option value="PAUSED">Paused — review before activating</option>
                <option value="ACTIVE">Active — go live immediately</option>
              </Select>
            </FormField>
          </Card>
        )}

        {/* Step 3 — Schedule */}
        {step === 2 && (
          <Card>
            <SectionHeader title="Schedule" subtitle="Leave blank to start immediately and run indefinitely" icon={<CalendarClock size={16} />} />
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Start time (optional)">
                <Input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </FormField>
              <FormField label="Stop time (optional)">
                <Input type="datetime-local" value={stopTime} onChange={(e) => setStopTime(e.target.value)} />
              </FormField>
            </div>
          </Card>
        )}

        {/* Step 4 — Review */}
        {step === 3 && (
          <Card>
            <SectionHeader title="Review & create" icon={<ClipboardList size={16} />} />
            <div className="divide-y divide-border">
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Name</div>
                <div className="font-semibold text-text-primary">{name}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Objective</div>
                <div className="font-semibold text-text-primary">
                  {OBJECTIVES.find((o) => o.value === objective)?.label ?? objective}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Special category</div>
                <div className="font-semibold text-text-primary">
                  {SPECIAL_CATEGORIES.find((c) => c.value === specialCategory)?.label ?? specialCategory}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Budget</div>
                <div className="tabular-nums font-semibold text-text-primary">
                  {currency} {parseFloat(budgetMajor || '0').toFixed(2)} / {budgetMode}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Status</div>
                <div>
                  <CampaignStatusPill status={status} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                <div className="text-text-muted">Schedule</div>
                <div className="font-semibold text-text-primary">
                  {startTime ? new Date(startTime).toLocaleString() : 'Immediately'}
                  {stopTime ? ` → ${new Date(stopTime).toLocaleString()}` : ' (no end)'}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Bottom navigation */}
        <div className="flex justify-between">
          <Button
            variant="ghost"
            disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
          >
            Back
          </Button>
          {step < 3 ? (
            <Button
              disabled={!stepValid(step)}
              onClick={() => setStep((s) => s + 1)}
              iconRight={<ChevronRight size={14} />}
            >
              Next: {STEPS[step + 1].label}
            </Button>
          ) : (
            <Button loading={busy} onClick={submit}>
              Create campaign
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Shows the user up-front what ad sets they'll be able to create under this
 * campaign — optimization goals + (for conversion-based objectives) the
 * pixel events Meta will accept. Sourced from the meta-matrix so it
 * automatically reflects whatever live-tested rules we've encoded.
 */
const CascadePreview: React.FC<{ objective: OdaxObjective }> = ({ objective }) => {
  const goals = optGoalsForObjective(objective);
  const pixelEvents = pixelEventsForObjective(objective);
  return (
    <div className="mt-2 rounded-xl border border-border bg-surface-alt/40 p-3.5">
      <div className="mb-2 flex items-center gap-2">
        <Info size={13} className="text-info" />
        <p className="text-xs font-semibold text-text-primary">
          What this objective enables
        </p>
      </div>
      <div className="space-y-2 text-2xs text-text-secondary">
        <div>
          <span className="text-text-muted">Optimization goals you can pick on ad sets:</span>{' '}
          <span className="font-mono text-text-primary">
            {goals.length === 0 ? '—' : goals.map(goalLabel).join(' · ')}
          </span>
        </div>
        {pixelEvents.length > 0 && (
          <div>
            <span className="text-text-muted">Pixel events Meta will accept here:</span>{' '}
            <span className="font-mono text-text-primary">{pixelEvents.join(' · ')}</span>
          </div>
        )}
        {objective === 'OUTCOME_LEADS' && (
          <div className="text-text-muted">
            Lead Forms: 8 of 10 form shapes work today; <code className="font-mono">HIGHER_INTENT</code> needs Meta App-Review capability.
          </div>
        )}
      </div>
    </div>
  );
};
